import { ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useWorkspace } from '@/features/Workspaces/state/WorkspaceContext';
import { SelectWorkspacePlaceholder } from '@/features/Workspaces/components/SelectWorkspacePlaceholder';
import { NoWorkspacePlaceholder } from '@/features/Workspaces/components/NoWorkspacePlaceholder';
import { useViewScope, type ViewScope } from '@/shared/hooks/use-view-scope';
import { useIsAdmin } from '@/shared/hooks/use-admin-validation';
import { useIsSuperAdminRole } from '@/shared/hooks/use-system-role';
import { LoadingSpinner } from '@/shared/components/ui/loading-spinner';

interface WorkspaceGuardProps {
  children: ReactNode;
  /** Scopes allowed to render the children. Defaults to product-only. */
  allowedScopes?: ViewScope[];
}

export function WorkspaceGuard({ children, allowedScopes = ['product'] }: WorkspaceGuardProps) {
  const { currentWorkspace, products } = useWorkspace();
  const { scope, isResolving } = useViewScope();
  const { isAdmin } = useIsAdmin();
  const { isSuperAdmin } = useIsSuperAdminRole();
  const isUserOnly = !isAdmin && !isSuperAdmin;

  // user role só pode operar em product scope, independente do que a rota
  // permite — MENOS na visão pessoal, que é o espaço dela e não depende de
  // papel nenhum (AB#85637). Sem esta exceção, quem não administra nada
  // entraria na própria visão e seria mandado embora dela.
  const effectiveAllowed = isUserOnly
    ? allowedScopes.filter(s => s === 'product' || s === 'user')
    : allowedScopes;

  if (effectiveAllowed.includes(scope)) {
    return <>{children}</>;
  }

  // user role com workspace já selecionado: escopo pode estar temporariamente como
  // 'organization' enquanto useOrganization() ainda não resolveu o orgId.
  // Renderizar filhos para evitar o fallback Navigate que deixa a tela em branco.
  if (isUserOnly && currentWorkspace && effectiveAllowed.includes('product')) {
    return <>{children}</>;
  }

  // user role com 2+ produtos e nenhum selecionado: aguardar seleção
  if (isUserOnly && effectiveAllowed.includes('product') && products.length > 0 && !currentWorkspace) {
    return <SelectWorkspacePlaceholder />;
  }

  // user role sem produtos: mostrar tela de criação de workspace
  if (isUserOnly && effectiveAllowed.includes('product') && products.length === 0) {
    return <NoWorkspacePlaceholder />;
  }

  // Admin/superadmin sem produtos: deixa o destino mostrar empty state
  if (effectiveAllowed.includes('product') && products.length === 0) {
    return <>{children}</>;
  }

  // Scope ainda sendo resolvido (ex: no refresh do navegador, antes do perfil/org
  // carregar). Aguardar em vez de redirecionar — caso contrário o scope transitório
  // ('organization'/'platform') jogaria o usuário pra dashboard da organização,
  // perdendo a rota atual. (BUG #81698)
  if (isResolving) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Escopo incompatível com a rota (ex: estava em /agents e mudou pra
  // org-view): volta pra casa DAQUELE escopo.
  //
  // Na visão pessoal a casa é Skills, e não o painel: o painel não existe no
  // nível pessoal, então mandar para `/` faria o guarda de lá recusar e
  // devolver para cá — um laço de redirecionamento, não uma tela errada.
  return <Navigate to={scope === 'user' ? '/skills' : '/'} replace />;
}
